import {useEffect, useMemo} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import {connect} from 'react-redux'

import {index, show} from '../../../actions'

import {makeGetVenueById, makeGetPerformanceById} from '../../../selectors'

import {day, formatDate, formatTime, formatMoney} from '../../../utils'

import GlobeIcon from '@mui/icons-material/Language'

import EventLineup from './EventLineup'
import PerformanceButtons from './PerformanceButtons'
import EventAddress from './EventAddress'


import { 
  Tags, 
  // ViewCount,
  WebsiteLink, 
  DetailPageSection,
  InfoExpandingText,
  DetailPageTitle, 
} from '../../generics'



const styles = {
  root: css`
    display: flex;
    flex-direction: column;
    max-width: 550px;
    height: 100%;
  `,
  titleContainer: css`
    display: flex;
    justify-content: flex-start;
    gap: 20px;

    @media (max-width: 1120px) {
      justify-content: space-between;
      padding: 0 25px;
    }
    @media (max-width: 768px) {
      gap: 10px;
      padding: 0 10px 0 25px;
    }
  `,
  innerTitle: css`max-width: 80vw;`,
  dateTime: css`
    color: rgb(62, 166, 255);
    margin: 0 0 10px 0;
    font-weight: 600;
    pre {
      font-family: neue-haas-unica, sans-serif;
      margin: 0;
      line-height: 1.1; 
      text-transform: uppercase; 
    }
    @media (max-width: 1120px) { padding: 0 25px; }
  `,
  section: css`
    @media (max-width: 1120px) { padding: 0px 25px; }
  `,
  buttons: css`
    @media (max-width: 1120px) { padding: 0px 25px; }
  `,
  body: css`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    margin: 10px 0 20px;
    gap: 25px;

    @media (max-width: 1120px) {
      gap: 15px;
    }
  `,
  footer: css`
    @media (max-width: 1120px) { padding: 0 25px; }
  `
}


const PerformanceInfo = ({
  performanceId,
  tags,
  name,
  price,
  start,
  index,
  show,
  venue,
  venueId,
  website,
  address,
  location,
  permission,
  venueImages = [],
  description,
  descriptionPlainText,
  areVenueImagesLoaded
}) => {

 // Fetch venue images on mount
  useEffect(() => {
    if (venueId && !areVenueImagesLoaded) {
      index(`/venues/${venueId}/images`, 'VENUE_IMAGES')
    }
  }, [venueId, areVenueImagesLoaded, index])


  // Fetch venue images on mount
  useEffect(() => {
    if (!venue && venueId) {
      show('/venues/' + venueId, 'VENUE').catch(error => {})
    }
  }, [venue, venueId, show])


  // Format date/time string
  const dateTimeText = useMemo(() => {
    const date      = formatDate(start, 'MMM do')
    const dayName   = day(start, 'eee')
    const timeStart = formatTime(start)
    return `${dayName}  •  ${date}  •  ${timeStart}`
  }, [start])



  return (
    <div css={styles.root}>
      <div css={styles.titleContainer}>
        <div css={styles.innerTitle}>
          <DetailPageTitle
            caption={`event${price > 0 ? ` • ${formatMoney(price)}` : ''}`}
            title={name}
          />
        </div>
      </div>

      <div css={styles.dateTime}>
        <pre>{dateTimeText}</pre>
      </div>

      <div css={styles.buttons}>
        <PerformanceButtons performanceId={performanceId} />
      </div>

      <div css={styles.body}>
        <EventAddress
          venue={venue}
          images={venueImages}
          address={address}
          location={location}
        />

        {(description || descriptionPlainText) &&
          <div css={styles.section}>
            <InfoExpandingText text={description || descriptionPlainText} />
          </div>
        }

        <div css={styles.lineupContainer}>
          <EventLineup performanceId={performanceId} />
        </div>

        {website && (
          <div css={styles.section}>
            <DetailPageSection
              icon={<GlobeIcon />}
              Content={<WebsiteLink url={website} />}
            />
          </div>
        )}
      </div>

      <div css={styles.footer}>
        <Tags tags={tags} margin='top' />
      </div>
    </div>
  )
}



const makeMapStateToProps = () => {
  const selectVenue = makeGetVenueById()
  const selectPerf  = makeGetPerformanceById()

  return (state, {performanceId}) => {
    const perf = selectPerf(state, performanceId)?.attributes || {}
    const role = state.user?.attributes?.role
    const permission = ['admin', 'editor'].includes(role) ? 'owner' : perf.permission

    const venue  = selectVenue(state, perf.venueId)
    const images = venue?.attributes?.images ?? []


    return {
      venue,
      permission,
      performanceId,
      tags: perf.tags,
      name: perf.name,
      price: perf.price,
      start: perf.start,
      website: perf.website,
      venueId: perf.venueId,
      location: perf.location,
      address: perf.address,
      description: perf.description,
      descriptionPlainText: perf.descriptionPlainText,
      venueImages: images,
      areVenueImagesLoaded: images.length > 0
    }
  }
}


export default connect(makeMapStateToProps, {index, show})(PerformanceInfo)


